import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { PlusIcon, XMarkIcon, UserIcon, CalendarDaysIcon } from '@heroicons/react/24/outline';

interface ActivityLog {
  id: number;
  claim_id: number;
  action: string;
  description: string;
  performed_by: string;
  created_at: string;
}

interface ClientClaimNotesProps {
  claimId: string;
}

const API_URL = process.env.REACT_APP_API_URL || '';

const ClientClaimNotes: React.FC<ClientClaimNotesProps> = ({ claimId }) => {
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAddNote, setShowAddNote] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (claimId) fetchActivityLogs();
  }, [claimId]);

  const fetchActivityLogs = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const response = await axios.get(
        `${API_URL}/api/claim-notes/${claimId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setActivityLogs(response.data && Array.isArray(response.data) ? response.data : []);
    } catch (error: any) {
      console.error('Error fetching activity logs:', error);
      setError('Failed to load activity logs');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) {
      setError('Please enter a note');
      return;
    }

    setIsSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      let performedBy = 'System';
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          const user = JSON.parse(storedUser);
          const name = user.username || user.email || 'System';
          const surname = user.surname || '';
          performedBy = `${name} ${surname}`.trim() || name || 'System';
        } catch (e) {
          performedBy = 'System';
        }
      }

      await axios.post(
        `${API_URL}/api/claim-notes`,
        {
          claim_id: parseInt(claimId),
          action: 'note_added',
          description: newNote.trim(),
          performed_by: performedBy
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccess('Note added successfully');
      setNewNote('');
      setShowAddNote(false);
      await fetchActivityLogs();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error: any) {
      console.error('Error adding note:', error);
      setError(error.response?.data?.message || 'Failed to add note');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDateTime = (dateString: string): string => {
    return new Date(dateString).toLocaleString('en-ZA', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'note_added': return '📝';
      case 'status_changed': return '🔄';
      case 'policy_updated': return '📄';
      case 'method_changed': return '✉️';
      case 'type_changed': return '📋';
      case 'date_updated': return '📅';
      case 'signed_off': return '✅';
      default: return '📌';
    }
  };

  const getActionLabel = (action: string) => {
    switch (action) {
      case 'note_added': return 'Added a note';
      case 'status_changed': return 'Changed status';
      case 'policy_updated': return 'Updated policy';
      case 'method_changed': return 'Changed method';
      case 'type_changed': return 'Changed type';
      case 'date_updated': return 'Updated date';
      case 'signed_off': return 'Signed off';
      default: return action.replace(/_/g, ' ');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h4 className="text-lg font-semibold text-gray-800">Activity Log</h4>
          <p className="text-sm text-gray-500 mt-1">
            {activityLogs.length} activity {activityLogs.length !== 1 ? 'entries' : 'entry'}
          </p>
        </div>
        <button
          onClick={() => setShowAddNote(!showAddNote)}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
        >
          <PlusIcon className="w-4 h-4 mr-2" />
          Add Note
        </button>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
          {error}
          <button onClick={() => setError('')} className="float-right text-red-700 hover:text-red-900">×</button>
        </div>
      )}
      {success && (
        <div className="p-4 bg-green-50 border border-green-200 text-green-700 rounded-lg">
          {success}
        </div>
      )}

      {showAddNote && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex justify-between items-start mb-3">
            <label className="text-sm font-medium text-gray-700">Add a note</label>
            <button
              onClick={() => { setShowAddNote(false); setNewNote(''); setError(''); }}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
          <textarea
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Enter your note here..."
          />
          <div className="flex justify-end space-x-3 mt-3">
            <button
              onClick={() => { setShowAddNote(false); setNewNote(''); setError(''); }}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleAddNote}
              disabled={isSubmitting || !newNote.trim()}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Saving...' : 'Add Note'}
            </button>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="text-center py-12">
          <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <svg className="animate-spin w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
          <p className="text-gray-600">Loading activity log...</p>
        </div>
      ) : activityLogs.length === 0 ? (
        <div className="text-center py-12">
          <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <UserIcon className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No activity yet</h3>
          <p className="text-gray-500 max-w-md mx-auto">Add a note to start tracking activity for this claim.</p>
        </div>
      ) : (
        <div className="flow-root">
          <ul className="-mb-8">
            {activityLogs.map((log, index) => (
              <li key={log.id}>
                <div className="relative pb-8">
                  {index < activityLogs.length - 1 && (
                    <span className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true"></span>
                  )}
                  <div className="relative flex space-x-3">
                    <div>
                      <span className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center ring-8 ring-white">
                        <span className="text-lg">{getActionIcon(log.action)}</span>
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{getActionLabel(log.action)}</p>
                          <p className="text-sm text-gray-600 mt-0.5">{log.description}</p>
                          <div className="flex items-center mt-2">
                            <UserIcon className="w-3 h-3 text-gray-400 mr-1" />
                            <span className="text-xs text-gray-500">{log.performed_by || 'System'}</span>
                            <span className="mx-2 text-gray-300">•</span>
                            <CalendarDaysIcon className="w-3 h-3 text-gray-400 mr-1" />
                            <span className="text-xs text-gray-500">{formatDateTime(log.created_at)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ClientClaimNotes;